'use client';

import {useEffect, useMemo, useRef, useState} from 'react';
import {Decoration} from '@/_application/modules/not-found/types/not-found.types';

export type MovingItem = {
    x: number;
    y: number;
    dx: number;
    dy: number;
    width: number;
    height: number;
    rotation: number;
};

const randomBetween = (min: number, max: number) => Math.random() * (max - min) + min;

type UseMovingDecorationsParams = {
    decorations: Decoration[];
    defaultWidth?: number;
    defaultHeight?: number;
};

export const useMovingDecorations = ({
    decorations,
    defaultWidth = 130,
    defaultHeight = 130,
}: UseMovingDecorationsParams) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const itemsRef = useRef<MovingItem[]>([]);
    const [, forceRender] = useState(0);

    const normalizedDecorations = useMemo(
        () =>
            decorations.map(item => ({
                ...item,
                width: item.width ?? defaultWidth,
                height: item.height ?? defaultHeight,
            })),
        [decorations, defaultWidth, defaultHeight],
    );

    useEffect(() => {
        const container = containerRef.current;
        if (!container || normalizedDecorations.length === 0) return;

        const initItems = () => {
            const rect = container.getBoundingClientRect();
            const containerWidth = rect.width;
            const containerHeight = rect.height;

            itemsRef.current = normalizedDecorations.map(item => {
                const width = item.width ?? defaultWidth;
                const height = item.height ?? defaultHeight;

                const maxX = Math.max(containerWidth - width, 0);
                const maxY = Math.max(containerHeight - height, 0);

                return {
                    x: randomBetween(0, maxX),
                    y: randomBetween(0, maxY),
                    dx: randomBetween(1.2, 2.8) * (Math.random() > 0.5 ? 1 : -1),
                    dy: randomBetween(1.2, 2.8) * (Math.random() > 0.5 ? 1 : -1),
                    width,
                    height,
                    rotation: randomBetween(-20, 20),
                };
            });

            forceRender(v => v + 1);
        };

        const animate = () => {
            const rect = container.getBoundingClientRect();
            const containerWidth = rect.width;
            const containerHeight = rect.height;

            itemsRef.current = itemsRef.current.map(item => {
                let nextX = item.x + item.dx;
                let nextY = item.y + item.dy;
                let nextDx = item.dx;
                let nextDy = item.dy;

                if (nextX <= 0) {
                    nextX = 0;
                    nextDx = Math.abs(nextDx);
                } else if (nextX + item.width >= containerWidth) {
                    nextX = Math.max(containerWidth - item.width, 0);
                    nextDx = -Math.abs(nextDx);
                }

                if (nextY <= 0) {
                    nextY = 0;
                    nextDy = Math.abs(nextDy);
                } else if (nextY + item.height >= containerHeight) {
                    nextY = Math.max(containerHeight - item.height, 0);
                    nextDy = -Math.abs(nextDy);
                }

                return {
                    ...item,
                    x: nextX,
                    y: nextY,
                    dx: nextDx,
                    dy: nextDy,
                };
            });

            forceRender(v => v + 1);
            animationFrameRef.current = window.requestAnimationFrame(animate);
        };

        initItems();
        animationFrameRef.current = window.requestAnimationFrame(animate);

        const resizeObserver = new ResizeObserver(() => {
            initItems();
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();

            if (animationFrameRef.current) {
                window.cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [normalizedDecorations, defaultWidth, defaultHeight]);

    return {
        containerRef,
        items: itemsRef.current,
        decorations: normalizedDecorations,
    };
};
